import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'

import { AppButton } from '@/components'
import {
  useCreateGoalTransferOut,
  useGetEstimateSavingTransferOutFee,
} from '@/domain'
import { useSavingsStore } from '@/store/useSavingsStore'
import { useUserStore } from '@/store/useUserStore'
import { currencyUtils } from '@/utils'

const { formatFiatCurrency, cryptoToFiat, removeTrailingZeros } = currencyUtils

export const CreateTransferOutButton: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const goalTransferOut = useSavingsStore((state) => state.goalTransferOut)
  const completeCurrencies = useUserStore((s) => s.wallet?.completeCurrencies)

  // Находим USDT в кошельке пользователя
  const usdtWallet = completeCurrencies?.find(
    (currency: any) => currency.code === 'USDT',
  )

  const totalAmountToFiat = cryptoToFiat({
    amountCrypto: Number(
      goalTransferOut.total_amount?.amount ||
        goalTransferOut.cryptoAmount.amount,
    ),
    cryptoPricePerUnit: usdtWallet?.pricePerUnit || 0,
  })

  const formattedFiatAmount = formatFiatCurrency({
    value: Number(totalAmountToFiat),
    fiatCode: goalTransferOut.fiatAmount.code as any,
    showDecimals: true,
  })

  const redirectToTransferOutSuccess = () => {
    navigate('/savings/transfer-out/success', {
      state: {
        amount: removeTrailingZeros(
          goalTransferOut.total_amount?.amount ||
            goalTransferOut.cryptoAmount.amount,
        ),
        code:
          goalTransferOut.total_amount?.code ||
          goalTransferOut.cryptoAmount.code,
        title: t('screens:transferOut.youTransferred'),
        description: t('screens:transferOut.youTransferredDescription'),
        relativeFiatFormatted: formattedFiatAmount,
        currencyLabel: usdtWallet?.name || 'USDT',
        doneRoute: `/savings/savings-detail/${goalTransferOut.saving_goal_id}`,
        navigationMethod: 'dismiss' as const,
      },
    })
  }

  // Проверяем загрузку комиссий
  const { isLoading: isLoadingTransferOutFees } =
    useGetEstimateSavingTransferOutFee({
      amount: Number(goalTransferOut.cryptoAmount.amount),
      currency: goalTransferOut.cryptoAmount.code as any,
      transfer_type: 'regular', // По умолчанию regular
    })

  const { mutateAsync: createTransferOut, isPending: isLoadingTransferOut } =
    useCreateGoalTransferOut({
      onSuccess: () => {
        redirectToTransferOutSuccess()
      },
      onError: ({ errorMessage }) => {
        toast.error(errorMessage || t('api:errors.somethingWrong'))
      },
    })

  const handleCreateTransferOut = async () => {
    if (!goalTransferOut.saving_request_id) {
      toast.error(t('api:errors.somethingWrong'))
      return
    }

    try {
      await createTransferOut({
        saving_goal_id: goalTransferOut.saving_goal_id,
        saving_request_id: goalTransferOut.saving_request_id,
      })
    } catch (error) {
      console.error('Transfer failed:', error)
      // Error handling is done in the hook
    }
  }

  return (
    <AppButton
      label={t('form:actions.confirm')}
      isLoading={isLoadingTransferOut || isLoadingTransferOutFees}
      onPress={handleCreateTransferOut}
      disabled={!goalTransferOut.saving_request_id}
    />
  )
}

export default CreateTransferOutButton
